import React, { useCallback, useEffect, useRef, useState } from 'react';
import { sendRequest } from '../ApiRequest';
import { sendCachedRequest } from '../utils/apiCache';
import { BASE_URL } from '../URL/constants';
import { useNavigate } from 'react-router-dom';
import useHtmlLinkNavigation from '../hooks/useHtmlLinkNavigation';
import SignupModal from './buttons/SignupModal';
import './Header.css';

const DYNAMIC_SERVICES_STYLE = `
  .services-dropdown-grid {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) minmax(280px, 1fr);
    gap: 1.25rem;
    min-width: 460px;
  }

  .services-dropdown-grid[data-dropdown-context="mobile"] {
    grid-template-columns: 1fr;
    min-width: auto;
  }

  .services-categories-column {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 340px;
    overflow-y: auto;
  }

  .services-category-button {
    border: 1px solid rgba(212, 20, 90, 0.25);
    border-radius: 10px;
    padding: 0.6rem 0.85rem;
    background: #ffffff;
    color: #2C2C2C;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    width: 100%;
  }

  .services-category-button:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: rgba(212, 20, 90, 0.05);
  }

  .services-category-button.active {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: linear-gradient(135deg, rgba(212, 20, 90, 0.08), rgba(176, 16, 80, 0.05));
    box-shadow: 0 6px 15px rgba(212, 20, 90, 0.15);
  }

  .services-items-column {
    border-left: 1px solid rgba(0, 0, 0, 0.05);
    padding-left: 1rem;
  }

  .services-dropdown-grid[data-dropdown-context="mobile"] .services-items-column {
    border-left: none;
    padding-left: 0;
  }

  .services-items-grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .services-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.65rem 0.85rem;
    border-radius: 10px;
    background: rgba(212, 20, 90, 0.05);
    font-weight: 500;
    color: #2C2C2C;
  }

  .services-item-price {
    font-weight: 600;
    color: var(--primary-color);
    margin-left: 1rem;
    white-space: nowrap;
  }

  .services-state {
    font-size: 0.9rem;
    color: #5C5C5C;
    padding: 0.25rem 0;
  }

  @media (max-width: 768px) {
    .services-dropdown-grid {
      min-width: auto;
    }
  }
`;

const MOBILE_MENU_TWEAKS_STYLE = `
  .header-wrapper .mobile-menu-logo {
    display: none !important;
  }

  .header-wrapper .mobile-menu-header {
    position: relative;
    padding-right: 40px;
  }

  .header-wrapper .mobile-menu-header .mobile-close-btn {
    position: absolute;
    right: 8px;
    top: 8px;
  }

  .header-wrapper .mobile-menu nav a {
    text-align: left;
  }

  .header-wrapper .mobile-menu .mobile-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
`;

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
});

const escapeHtml = (value = '') =>
  value
    .toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatCurrency = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '';
  return currencyFormatter.format(numericValue);
};

const hoistHeadStylesToBody = (doc) => {
  if (!doc?.head || !doc?.body) return;
  const styles = Array.from(doc.head.querySelectorAll('style'));
  if (!styles.length) return;
  styles.forEach((styleNode) => {
    const clone = styleNode.cloneNode(true);
    doc.body.insertBefore(clone, doc.body.firstChild);
  });
};

const ensureServicesPlaceholders = (doc) => {
  if (!doc) return;
  const dropdown = doc.querySelector('.navbar-dropdown');
  if (dropdown && !dropdown.querySelector('.dropdown-menu')) {
    const menu = doc.createElement('div');
    menu.className = 'dropdown-menu';
    dropdown.appendChild(menu);
  }

  const mobileDropdownWrapper = doc.querySelector('.mobile-dropdown');
  if (mobileDropdownWrapper && !doc.getElementById('mobileDropdownMenu')) {
    const mobileMenu = doc.createElement('div');
    mobileMenu.className = 'mobile-dropdown-menu';
    mobileMenu.id = 'mobileDropdownMenu';
    mobileDropdownWrapper.appendChild(mobileMenu);
  }
};

const injectServicesStyle = (doc) => {
  if (!doc?.body) return;
  doc.querySelectorAll('[data-dynamic-services-style]').forEach((node) => node.remove());
  const styleTag = doc.createElement('style');
  styleTag.setAttribute('data-dynamic-services-style', 'true');
  styleTag.textContent = DYNAMIC_SERVICES_STYLE;
  doc.body.insertBefore(styleTag, doc.body.firstChild);
};

const buildCategoryButtonsMarkup = ({ categories, activeCategoryId, categoriesLoading, categoriesError }) => {
  if (categoriesLoading) {
    return '<div class="services-state">Loading categories…</div>';
  }

  if (categoriesError) {
    return `<div class="services-state">${escapeHtml(categoriesError)}</div>`;
  }

  if (!categories.length) {
    return '<div class="services-state">Categories will appear here soon.</div>';
  }

  return categories
    .map((category) => {
      const id = category?.id ?? '';
      const name = escapeHtml(category?.name || 'Category');
      const isActive = String(id) === String(activeCategoryId);
      return `
        <button
          type="button"
          class="services-category-button${isActive ? ' active' : ''}"
          data-services-category
          data-category-id="${id}"
        >
          ${name}
        </button>
      `;
    })
    .join('');
};

const buildServicesMarkup = ({
  context,
  categories,
  activeCategoryId,
  servicesByCategory,
  servicesLoadingCategory,
  servicesErrors,
  categoriesLoading
}) => {
  if (categoriesLoading) {
    return '<div class="services-state">Loading services…</div>';
  }

  if (!activeCategoryId) {
    return '<div class="services-state">Choose a category to preview services.</div>';
  }

  if (servicesLoadingCategory === activeCategoryId) {
    return '<div class="services-state">Fetching fresh services…</div>';
  }

  if (servicesErrors[activeCategoryId]) {
    return `<div class="services-state">${escapeHtml(servicesErrors[activeCategoryId])}</div>`;
  }

  const services = servicesByCategory[activeCategoryId] || [];
  if (!services.length) {
    return '<div class="services-state">Services will be available for this category soon.</div>';
  }

  const limit = context === 'desktop' ? 8 : 6;
  const limitedServices = services.slice(0, limit);
  const activeCategoryName =
    categories.find((cat) => String(cat?.id) === String(activeCategoryId))?.name || 'the selected category';

  return `
    <div class="services-state" style="font-weight:600; color:#2C2C2C; padding-bottom:0.35rem;">
      Popular in ${escapeHtml(activeCategoryName)}
    </div>
    <ul class="services-items-grid">
      ${limitedServices
        .map((service) => {
          const priceText = formatCurrency(service.price);
          return `
            <li class="services-item">
              <span>${escapeHtml(service.name || 'Service')}</span>
              ${priceText ? `<span class="services-item-price">${escapeHtml(priceText)}</span>` : ''}
            </li>
          `;
        })
        .join('')}
    </ul>
  `;
};

const buildDropdownMarkup = (context, config) => `
  <div class="services-dropdown-grid" data-dropdown-context="${context}">
    <div class="services-categories-column">
      ${buildCategoryButtonsMarkup(config)}
    </div>
    <div class="services-items-column">
      ${buildServicesMarkup({ ...config, context })}
    </div>
  </div>
`;

const applyDynamicServicesToDoc = (doc, config) => {
  if (!doc) return;
  injectServicesStyle(doc);

  const dropdown = doc.querySelector('.navbar-dropdown .dropdown-menu');
  if (dropdown) {
    dropdown.setAttribute('data-services-dropdown', 'desktop');
    dropdown.innerHTML = buildDropdownMarkup('desktop', config);
  }

  const mobileMenu = doc.getElementById('mobileDropdownMenu');
  if (mobileMenu) {
    mobileMenu.setAttribute('data-services-dropdown', 'mobile');
    mobileMenu.innerHTML = buildDropdownMarkup('mobile', config);
  }
};

const Header = () => {
  const [headerHtml, setHeaderHtml] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [servicesByCategory, setServicesByCategory] = useState({});
  const [servicesLoadingCategory, setServicesLoadingCategory] = useState(null);
  const [servicesErrors, setServicesErrors] = useState({});
  const [headerTemplateVersion, setHeaderTemplateVersion] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const headerBaseHtmlRef = useRef('');

  const navigate = useNavigate();

  const replaceFallbackName = useCallback((doc, companyName) => {
    if (!doc || !companyName) return;
    const FALLBACK = 'Looks Salon';
    if (FALLBACK.toLowerCase() === companyName.toLowerCase()) return;

    doc.querySelectorAll('.logo-text').forEach((node) => {
      if (node.textContent?.trim() === FALLBACK) {
        node.textContent = companyName;
      }
    });

    doc.querySelectorAll('*').forEach((node) => {
      if (node.childNodes?.length) {
        node.childNodes.forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE && child.textContent?.includes(FALLBACK)) {
            child.textContent = child.textContent.replace(new RegExp(FALLBACK, 'g'), companyName);
          }
        });
      }
    });
  }, []);

  const applyCompanyName = useCallback((doc, companyName, sectionLabel = 'Header') => {
    if (!doc || !companyName) return;

    doc.querySelectorAll('[data-company-name]').forEach((node) => {
      node.textContent = companyName;
    });

    const titleNode = doc.querySelector('[data-company-name-title]');
    if (titleNode) {
      titleNode.textContent = `${companyName} - ${sectionLabel}`;
      document.title = titleNode.textContent;
    } else {
      document.title = companyName;
    }

    replaceFallbackName(doc, companyName);
  }, [replaceFallbackName]);

  const applyCompanyLogo = useCallback((doc, logoUrl) => {
    if (!doc || !logoUrl) return;

    // Replace logo images in various possible locations
    // 1. Images inside .logo containers
    doc.querySelectorAll('.logo img').forEach((img) => {
      img.src = logoUrl;
      img.setAttribute('src', logoUrl);
    });

    // 2. Images with logo-related src attributes (bill.jpg, logo, assets/images)
    doc.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src') || '';
      // Replace if it's a logo-related path
      if (src.includes('assets/images/bill') || 
          src.includes('bill.jpg') || 
          src.includes('/bill') ||
          (src.includes('assets/images') && (src.includes('logo') || src.includes('bill')))) {
        img.src = logoUrl;
        img.setAttribute('src', logoUrl);
      }
    });

    // 3. First image in header/main-header (likely the logo)
    const headerLogo = doc.querySelector('header img, .main-header img');
    if (headerLogo) {
      const src = headerLogo.getAttribute('src') || '';
      // Replace if it looks like a logo path or is in a logo container
      if (src.includes('bill') || src.includes('logo') || src.includes('assets/images') || 
          headerLogo.closest('.logo')) {
        headerLogo.src = logoUrl;
        headerLogo.setAttribute('src', logoUrl);
      }
    }
  }, []);

  const fetchHeaderContent = useCallback(async () => {
    try {
      const response = await sendCachedRequest(
        'POST',
        `${BASE_URL}/v1/public_site/public_site_info/get_list`,
        null,
        { cacheKey: 'public_site_info' }
      );

      const content = response.data.data.public_site_info.header_content?.body;
      if (!content) return;

      const companyInfo = response.data.data.company_info;
      const companyName = companyInfo?.name?.trim();
      const logoUrl = companyInfo?.logo_url;
      if (companyInfo) {
        window.__PUBLIC_SITE_COMPANY__ = companyInfo;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');

      // Normalize salon-specific mobile menu markup to work with shared JS
      const salonMobileToggle = doc.querySelector('.ls-mobile-toggle');
      if (salonMobileToggle) {
        salonMobileToggle.classList.add('mobile-menu-toggle');
        salonMobileToggle.removeAttribute('onclick'); // prevent lsToggleMobileMenu error
      }

      const salonMobileMenu = doc.getElementById('ls-mobile-menu');
      if (salonMobileMenu) {
        salonMobileMenu.classList.add('mobile-menu');
      }

      // Apply company logo dynamically
      if (logoUrl) {
        applyCompanyLogo(doc, logoUrl);
      }

      const signupBtn = Array.from(doc.querySelectorAll('a')).find(
        (a) => a.textContent.trim() === 'Signup'
      );

      const userDetails = JSON.parse(localStorage.getItem('userDetails'));
      const isUserLoggedIn = !!userDetails?.data?.customer?.id;
      setIsLoggedIn(isUserLoggedIn);

      if (signupBtn && isUserLoggedIn) {
        const customHTML = `
          <span class="d-flex align-items-center mx-1" id="user-wrapper">
            <a href="#" id="logout-btn" class="btn btn-sm" onclick="return false;">Logout</a>
          </span>
        `;
        signupBtn.insertAdjacentHTML('beforebegin', customHTML);
        signupBtn.remove();
      }

      // Ensure only one of Signup / Logout is visible based on login state
      const allLinks = Array.from(doc.querySelectorAll('a'));
      if (isUserLoggedIn) {
        allLinks.forEach((link) => {
          const text = link.textContent.trim().toLowerCase();
          if (text === 'signup') {
            link.style.display = 'none';
            link.style.visibility = 'hidden';
          }
        });
      } else {
        allLinks.forEach((link) => {
          const text = link.textContent.trim().toLowerCase();
          if (text === 'logout') {
            link.style.display = 'none';
            link.style.visibility = 'hidden';
          }
        });
      }

      // Show "My Orders" link if user is logged in
      const myOrderLinks = Array.from(doc.querySelectorAll('a')).filter(
        (a) => {
          const text = a.textContent.trim().toLowerCase();
          const href = a.getAttribute('href') || '';
          return text.includes('order') || 
                 text.includes('my order') ||
                 href.includes('/order');
        }
      );
      
      if (userDetails?.data?.customer?.id) {
        // User is logged in - show My Orders links
        myOrderLinks.forEach(link => {
          link.style.display = '';
          link.style.visibility = 'visible';
          link.classList.remove('d-none', 'hidden');
          link.removeAttribute('hidden');
        });
      } else {
        // User is not logged in - hide My Orders links
        myOrderLinks.forEach(link => {
          link.style.display = 'none';
          link.style.visibility = 'hidden';
          link.classList.add('d-none');
        });
      }

      applyCompanyName(doc, companyName, 'Header');
      hoistHeadStylesToBody(doc);
      ensureServicesPlaceholders(doc);

      // Inject small CSS tweaks for mobile dropdown without modifying stored HTML
      if (doc?.body) {
        doc.body.querySelectorAll('[data-mobile-menu-tweaks]').forEach((node) => node.remove());
        const tweaksStyle = doc.createElement('style');
        tweaksStyle.setAttribute('data-mobile-menu-tweaks', 'true');
        tweaksStyle.textContent = MOBILE_MENU_TWEAKS_STYLE;
        doc.body.insertBefore(tweaksStyle, doc.body.firstChild);
      }
      headerBaseHtmlRef.current = doc.body.innerHTML;
      setHeaderTemplateVersion((prev) => prev + 1);
      window.dispatchEvent(new Event('cart-updated'));
      setHeaderHtml(headerBaseHtmlRef.current);
    } catch (error) {
      console.error('Error fetching header content:', error);
    }
  }, [applyCompanyName, applyCompanyLogo]);

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const response = await sendCachedRequest(
        'GET',
        `${BASE_URL}/v1/public_site/menus/fetch_categories`,
        null,
        { cacheKey: 'menu_categories' }
      );
      const fetchedCategories = Array.isArray(response?.data?.categories)
        ? response.data.categories
        : [];
      setCategories(fetchedCategories);
    } catch (catError) {
      console.error('Failed to fetch categories:', catError);
      setCategories([]);
      setCategoriesError('Unable to load categories right now.');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const fetchServicesForCategory = useCallback(async (categoryId) => {
    if (!categoryId) return;
    setServicesErrors((prev) => ({ ...prev, [categoryId]: null }));
    setServicesLoadingCategory(categoryId);

    try {
      const response = await sendCachedRequest(
        'GET',
        `${BASE_URL}/v1/public_site/menus/item_lists`,
        null,
        {
          params: {
            category_id: categoryId,
            item_type: 'service',
            per_page: 200
          },
          cacheKey: `item_lists_services_${categoryId}`
        }
      );

      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      const normalizedServices = items.map((item) => {
        const rawName = item?.item_name;
        const name =
          typeof rawName === 'object'
            ? rawName?.en || Object.values(rawName).filter(Boolean)[0] || ''
            : rawName || '';
        return {
          id: item?.id,
          name,
          price: item?.sales_price
        };
      });

      setServicesByCategory((prev) => ({
        ...prev,
        [categoryId]: normalizedServices
      }));
      setServicesErrors((prev) => {
        const next = { ...prev };
        delete next[categoryId];
        return next;
      });
    } catch (serviceError) {
      console.error(`Failed to fetch services for category ${categoryId}:`, serviceError);
      setServicesErrors((prev) => ({
        ...prev,
        [categoryId]: 'Unable to load services at the moment.'
      }));
    } finally {
      setServicesLoadingCategory((prev) => (prev === categoryId ? null : prev));
    }
  }, []);

  const fetchCartItems = useCallback(async () => {
    const currentUser = JSON.parse(localStorage.getItem('userDetails'));
    setLoading(true);

    if (!currentUser?.data?.customer) {
      const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
      const guestItems = Array.isArray(guestCart)
        ? guestCart
        : Object.values(guestCart);
      setCartItems(guestItems);
      setLoading(false);
      return;
    }

    try {
      const response = await sendRequest(
        'GET',
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart`,
        null,
        { Authorization: currentUser.data.token }
      );
      setCartItems(response.data.cart_items || []);
    } catch (error) {
      console.error('Error fetching cart items:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effect to control SignupModal
  useEffect(() => {
    if (showLoginModal) {
      const modal = document.getElementById('signupModal');
      if (modal && typeof window !== 'undefined' && window.bootstrap) {
        const bootstrapModal = new window.bootstrap.Modal(modal);
        bootstrapModal.show();
        
        // Listen for modal close event
        const handleModalHidden = () => {
          setShowLoginModal(false);
          setShowOtpLogin(false);
          setPhoneNumber("");
          setOtp("");
          setFirstName("");
          setLastName("");
          setEmail("");
        };
        
        modal.addEventListener('hidden.bs.modal', handleModalHidden);
        
        // Cleanup function
        return () => {
          modal.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
      }
    }
  }, [showLoginModal]);

  useEffect(() => {
    fetchHeaderContent();
    fetchCartItems();  
    let timeout;
  
    const handleCartUpdated = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        fetchCartItems();
      }, 100);
    };
    
    const handleUserLoggedIn = async () => {
      // Refresh header when user logs in - this will update the HTML
      await fetchHeaderContent();
      
      // After header content is fetched and updated, directly update DOM if needed
      setTimeout(() => {
        const signupBtn = Array.from(document.querySelectorAll('a')).find(
          (a) => a.textContent.trim() === 'Signup'
        );
        
        const userDetails = JSON.parse(localStorage.getItem('userDetails'));
        const existingLogout = document.getElementById('logout-btn');
        
        // If Signup button still exists and user is logged in, replace it
        if (signupBtn && userDetails?.data?.customer?.id && !existingLogout) {
          setIsLoggedIn(true);
          
          const customHTML = `
            <span class="d-flex align-items-center mx-1" id="user-wrapper">
              <a href="#" id="logout-btn" class="btn btn-sm" onclick="return false;">Logout</a>
            </span>
          `;
          signupBtn.insertAdjacentHTML('beforebegin', customHTML);
          signupBtn.remove();
          
          // Re-attach logout button event listener
          // Note: The main useEffect will handle this, but we ensure it's set up here too
          const logoutBtn = document.getElementById('logout-btn');
          if (logoutBtn) {
            // Define handler function
            const handleLogoutClick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              localStorage.removeItem('userDetails');
              localStorage.removeItem('authToken');
              localStorage.setItem('cartState', JSON.stringify({}));
              setIsLoggedIn(false);
              setCartItems([]);
              window.dispatchEvent(new Event('cart-updated'));
              fetchCartItems();
              fetchHeaderContent();
              navigate('/');
            };
            // Remove any existing listener first
            logoutBtn.removeEventListener('click', handleLogoutClick);
            // Add the event listener
            logoutBtn.addEventListener('click', handleLogoutClick, { once: false });
          }
        }
        
        // Show "My Orders" link if user is logged in
        const myOrderLinks = Array.from(document.querySelectorAll('a')).filter(
          (a) => {
            const text = a.textContent.trim().toLowerCase();
            const href = a.getAttribute('href') || '';
            return text.includes('order') || 
                   text.includes('my order') ||
                   href.includes('/order');
          }
        );
        
        if (userDetails?.data?.customer?.id) {
          // User is logged in - show My Orders links
          myOrderLinks.forEach(link => {
            link.style.display = '';
            link.style.visibility = 'visible';
            link.classList.remove('d-none', 'hidden');
            link.removeAttribute('hidden');
          });
        } else {
          // User is not logged in - hide My Orders links
          myOrderLinks.forEach(link => {
            link.style.display = 'none';
            link.style.visibility = 'hidden';
            link.classList.add('d-none');
          });
        }
      }, 300);
    };
  
    window.addEventListener('cart-updated', handleCartUpdated);
    window.addEventListener('user-logged-in', handleUserLoggedIn);
  
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('cart-updated', handleCartUpdated);
      window.removeEventListener('user-logged-in', handleUserLoggedIn);
    };
  }, [fetchHeaderContent, fetchCartItems]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!categories.length || activeCategoryId) return;
    const firstCategory = categories.find((cat) => cat?.id !== undefined && cat?.id !== null);
    if (firstCategory) {
      setActiveCategoryId(String(firstCategory.id));
    }
  }, [categories, activeCategoryId]);

  useEffect(() => {
    if (!activeCategoryId) return;
    if (servicesByCategory[activeCategoryId]) return;
    fetchServicesForCategory(activeCategoryId);
  }, [activeCategoryId, servicesByCategory, fetchServicesForCategory]);

  const rebuildHeaderWithServices = useCallback(() => {
    if (!headerBaseHtmlRef.current || typeof document === 'undefined') return;

    try {
      const workingDoc = document.implementation.createHTMLDocument('Header');
      workingDoc.body.innerHTML = headerBaseHtmlRef.current;
      applyDynamicServicesToDoc(workingDoc, {
        categories,
        activeCategoryId,
        servicesByCategory,
        servicesLoadingCategory,
        servicesErrors,
        categoriesLoading,
        categoriesError
      });
      setHeaderHtml(workingDoc.body.innerHTML);
    } catch (hydrateError) {
      console.error('Failed to hydrate services dropdown:', hydrateError);
    }
  }, [
    activeCategoryId,
    categories,
    categoriesError,
    categoriesLoading,
    servicesByCategory,
    servicesErrors,
    servicesLoadingCategory
  ]);

  useEffect(() => {
    rebuildHeaderWithServices();
  }, [rebuildHeaderWithServices, headerTemplateVersion]);

  const emitCategorySelectionEvent = useCallback((categoryIdValue) => {
    if (typeof window === 'undefined' || !categoryIdValue) return;
    window.dispatchEvent(
      new CustomEvent('header-category-selected', {
        detail: { categoryId: categoryIdValue }
      })
    );
  }, []);

  const handleCategorySelection = useCallback((categoryIdValue) => {
    if (categoryIdValue === null || categoryIdValue === undefined) return;
    const normalizedId = String(categoryIdValue);
    setActiveCategoryId((prev) => (prev === normalizedId ? prev : normalizedId));

    if (typeof window !== 'undefined' && typeof window.closeMobileMenu === 'function') {
      window.closeMobileMenu();
    }

    const isOnHomePage = typeof window === 'undefined' ? true : window.location.pathname === '/';
    if (!isOnHomePage) {
      navigate('/', { state: { focusCategoryId: normalizedId } });
      return;
    }

    emitCategorySelectionEvent(normalizedId);
  }, [emitCategorySelectionEvent, navigate]);

  useEffect(() => {
    if (typeof window === 'undefined' || !headerHtml) return undefined;
    const categoryNodes = Array.from(document.querySelectorAll('[data-services-category]'));
    if (!categoryNodes.length) return undefined;

    const handleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const categoryId = event.currentTarget.getAttribute('data-category-id');
      handleCategorySelection(categoryId);
    };

    categoryNodes.forEach((node) => node.addEventListener('click', handleClick));
    return () => {
      categoryNodes.forEach((node) => node.removeEventListener('click', handleClick));
    };
  }, [headerHtml, handleCategorySelection]);

  useEffect(() => {
    if (typeof window === 'undefined' || !headerHtml) return undefined;
    
    const toggleMenu = () => {
      try {
        const menu =
          document.getElementById('mobileMenu') ||
          document.querySelector('.mobile-menu') ||
          document.getElementById('ls-mobile-menu');
        const overlay = document.querySelector('.mobile-menu-overlay');
        const toggles = document.querySelectorAll(
          '[data-mobile-menu-toggle], .mobile-menu-toggle, .menu-toggle'
        );
        
        if (!menu) {
          console.warn('Mobile menu element not found');
          return;
        }
        
        const isOpen = menu.classList.contains('open') || 
                       menu.classList.contains('show') ||
                       menu.style.display === 'flex';
        
        if (isOpen) {
          // Close menu
          document.body?.classList?.remove('mobile-menu-open');
          menu.classList.remove('open', 'show');
          menu.style.display = 'none';
          menu.setAttribute('aria-hidden', 'true');
          if (overlay) {
            overlay.classList.remove('active', 'show');
            overlay.style.display = 'none';
          }
          document.body.style.overflow = '';
        } else {
          // Open menu
          document.body?.classList?.add('mobile-menu-open');
          menu.classList.add('open', 'show');
          menu.style.display = 'flex';
          menu.setAttribute('aria-hidden', 'false');
          if (overlay) {
            overlay.classList.add('active', 'show');
            overlay.style.display = 'block';
          }
          document.body.style.overflow = 'hidden';
        }
        
        toggles.forEach((toggle) => {
          toggle.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
          toggle.classList.toggle('is-active', !isOpen);
        });
      } catch (err) {
        console.warn('toggleMobileMenu failed:', err);
      }
    };

    const closeMenu = () => {
      try {
        document.body?.classList?.remove('mobile-menu-open');
        const menu =
          document.getElementById('mobileMenu') ||
          document.querySelector('.mobile-menu') ||
          document.getElementById('ls-mobile-menu');
        if (menu) {
          menu.classList.remove('open', 'show');
          menu.style.display = 'none';
          menu.setAttribute('aria-hidden', 'true');
        }
        const overlay = document.querySelector('.mobile-menu-overlay');
        if (overlay) {
          overlay.classList.remove('active', 'show');
          overlay.style.display = 'none';
        }
        const toggles = document.querySelectorAll('[data-mobile-menu-toggle], .mobile-menu-toggle');
        toggles.forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.classList.remove('is-active');
        });
        document.body.style.overflow = '';
      } catch (err) {
        console.warn('closeMobileMenu failed:', err);
      }
    };

    // Expose functions to window object for inline onclick handlers
    window.toggleMobileMenu = toggleMenu;
    window.closeMobileMenu = closeMenu;

    // Direct listeners on specific elements (best effort)
    const mobileMenuToggle =
      document.querySelector('.mobile-menu-toggle') || document.querySelector('.menu-toggle');
    if (mobileMenuToggle) {
      // Remove existing onclick to avoid conflicts
      mobileMenuToggle.removeAttribute('onclick');
      mobileMenuToggle.addEventListener('click', toggleMenu);
    }

    const mobileMenuClose =
      document.querySelector('.mobile-menu-close') || document.querySelector('.mobile-close-btn');
    if (mobileMenuClose) {
      mobileMenuClose.removeAttribute('onclick');
      mobileMenuClose.addEventListener('click', closeMenu);
    }

    const overlay = document.querySelector('.mobile-menu-overlay');
    if (overlay) {
      overlay.removeAttribute('onclick');
      overlay.addEventListener('click', closeMenu);
    }

    // Global delegated handler so clicks on dynamically rendered buttons always work
    const delegatedClickHandler = (event) => {
      const toggleTarget = event.target.closest(
        '.menu-toggle, .mobile-menu-toggle, [data-mobile-menu-toggle]'
      );
      if (toggleTarget) {
        event.preventDefault();
        event.stopPropagation();
        toggleMenu();
        return;
      }

      const closeTarget = event.target.closest('.mobile-close-btn, .mobile-menu-close');
      if (closeTarget) {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
      }
    };

    document.addEventListener('click', delegatedClickHandler, true);

    return () => {
      if (mobileMenuToggle) {
        mobileMenuToggle.removeEventListener('click', toggleMenu);
      }
      if (mobileMenuClose) {
        mobileMenuClose.removeEventListener('click', closeMenu);
      }
      if (overlay) {
        overlay.removeEventListener('click', closeMenu);
      }
       document.removeEventListener('click', delegatedClickHandler, true);
      if (window.toggleMobileMenu === toggleMenu) {
        delete window.toggleMobileMenu;
      }
      if (window.closeMobileMenu === closeMenu) {
        delete window.closeMobileMenu;
      }
    };
  }, [headerHtml]);
  

  // Separate useEffect for updating cart badge - runs when cartItems changes
  useEffect(() => {
    const updateCartBadge = () => {
      // Find cart button - check multiple ways to find it
      const cartBtn = Array.from(document.querySelectorAll('a')).find(
        (a) => {
          const text = a.textContent.trim().toLowerCase();
          const href = (a.getAttribute('href') || '').toLowerCase();
          const className = (a.className || '').toLowerCase();
          return text === 'cart' || 
                 text.includes('cart') ||
                 href.includes('/cart') ||
                 className.includes('cart');
        }
      );

      if (cartBtn) {
        // Ensure cart button has relative positioning for absolute badge
        if (cartBtn.style.position !== 'relative' && !cartBtn.style.position) {
          cartBtn.style.position = 'relative';
        }
        
        let badge = cartBtn.querySelector('.badge, .cart-badge');
        
        // Calculate total cart count
        const totalCartCount = cartItems.length;

        if (totalCartCount > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'badge bg-danger ms-1 cart-badge';
            // Use absolute positioning to prevent layout shift
            badge.style.cssText = 'position: absolute; top: -8px; right: -8px; min-width: 18px; height: 18px; padding: 2px 6px; border-radius: 10px; font-size: 11px; font-weight: bold; text-align: center; background-color: #dc3545 !important; color: white !important; display: flex; align-items: center; justify-content: center; z-index: 10; line-height: 1;';
            cartBtn.appendChild(badge);
          }
          badge.textContent = totalCartCount;
          badge.style.display = 'flex';
          badge.style.visibility = 'visible';
        } else {
          if (badge) {
            badge.style.display = 'none';
            badge.style.visibility = 'hidden';
          }
        }
      } else {
        // If cart button not found, try to find it by href
        const cartLinkByHref = document.querySelector('a[href*="/cart"], a[href*="cart"]');
        if (cartLinkByHref) {
          // Ensure cart link has relative positioning for absolute badge
          if (cartLinkByHref.style.position !== 'relative' && !cartLinkByHref.style.position) {
            cartLinkByHref.style.position = 'relative';
          }
          
          let badge = cartLinkByHref.querySelector('.badge, .cart-badge');
          const totalCartCount = cartItems.length;

          if (totalCartCount > 0) {
            if (!badge) {
              badge = document.createElement('span');
              badge.className = 'badge bg-danger ms-1 cart-badge';
              // Use absolute positioning to prevent layout shift
              badge.style.cssText = 'position: absolute; top: -8px; right: -8px; min-width: 18px; height: 18px; padding: 2px 6px; border-radius: 10px; font-size: 11px; font-weight: bold; text-align: center; background-color: #dc3545 !important; color: white !important; display: flex; align-items: center; justify-content: center; z-index: 10; line-height: 1;';
              cartLinkByHref.appendChild(badge);
            }
            badge.textContent = totalCartCount;
            badge.style.display = 'flex';
            badge.style.visibility = 'visible';
          } else {
            if (badge) {
              badge.style.display = 'none';
              badge.style.visibility = 'hidden';
            }
          }
        }
      }
    };

    // Update badge immediately
    updateCartBadge();

    // Also update after a small delay to ensure DOM is ready
    const timeout = setTimeout(updateCartBadge, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [cartItems, headerHtml]); // Update when cartItems or headerHtml changes

  useEffect(() => {
    // Account Dropdown Management
    const updateAccountDropdown = () => {
      const accountDropdown = document.getElementById('accountDropdown');
      const accountLabel = document.getElementById('accountLabel');
      
      if (!accountDropdown) return;

      try {
        const userDetails = JSON.parse(localStorage.getItem('userDetails'));
        const isUserLoggedIn = userDetails && userDetails.data && userDetails.data.customer;

        if (isUserLoggedIn) {
          const customer = userDetails.data.customer;
          const userName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'User';
          const userEmail = customer.email || '';
          const userPhone = customer.phone || '';

          // Update account label
          if (accountLabel) {
            accountLabel.textContent = userName.split(' ')[0];
          }

          // Logged in dropdown
          accountDropdown.innerHTML = `
            <div class="account-dropdown-content">
              <div class="account-user-info">
                <div class="account-user-name">${userName}</div>
                ${userEmail ? `<div class="account-user-email">${userEmail}</div>` : ''}
                ${userPhone ? `<div class="account-user-phone">${userPhone}</div>` : ''}
              </div>
              <a href="/order" class="account-dropdown-item" id="myOrdersLink">
                <i class="fas fa-box"></i>
                <span>My Orders</span>
              </a>
              <a href="/wishlist" class="account-dropdown-item">
                <i class="far fa-heart"></i>
                <span>Wishlist</span>
              </a>
              <a href="/profile" class="account-dropdown-item">
                <i class="fas fa-user-circle"></i>
                <span>My Profile</span>
              </a>
              <a href="/addresses" class="account-dropdown-item">
                <i class="fas fa-map-marker-alt"></i>
                <span>Saved Addresses</span>
              </a>
              <div class="account-dropdown-divider"></div>
              <button class="logout-btn" id="account-logout-btn">
                <i class="fas fa-sign-out-alt"></i>
                <span>Logout</span>
              </button>
            </div>
          `;
        } else {
          // Update account label
          if (accountLabel) {
            accountLabel.textContent = 'Account';
          }

          // Not logged in dropdown
          accountDropdown.innerHTML = `
            <div class="account-dropdown-content">
              <div class="login-prompt">
                <p class="login-prompt-text">Please login to access your account</p>
                <button class="login-btn" id="account-login-btn" type="button">
                  Login / Sign Up
                </button>
              </div>
            </div>
          `;
          
          // Immediately attach click handler to the newly created button
          setTimeout(() => {
            const loginBtn = document.getElementById('account-login-btn');
            if (loginBtn) {
              console.log('Login button found in dropdown, attaching handler');
              loginBtn.onclick = (e) => {
                console.log('Login button clicked directly!');
                e.preventDefault();
                e.stopPropagation();
                
                // Close dropdown
                const dropdown = document.getElementById('accountDropdown');
                if (dropdown) {
                  dropdown.classList.remove('show');
                }
                
                // Open modal
                setShowLoginModal(true);
                setActiveTab("login");
                setShowOtpLogin(false);
                setPhoneNumber("");
                setOtp("");
                setFirstName("");
                setLastName("");
                setEmail("");
              };
            }
          }, 0);
        }
      } catch (e) {
        console.error('Error updating account dropdown:', e);
        // Default to not logged in
        if (accountLabel) {
          accountLabel.textContent = 'Account';
        }
        accountDropdown.innerHTML = `
          <div class="account-dropdown-content">
            <div class="login-prompt">
              <p class="login-prompt-text">Please login to access your account</p>
              <button class="login-btn" id="account-login-btn" type="button">
                Login / Sign Up
              </button>
            </div>
          </div>
        `;
        
        // Attach handler for error case too
        setTimeout(() => {
          const loginBtn = document.getElementById('account-login-btn');
          if (loginBtn) {
            loginBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              const dropdown = document.getElementById('accountDropdown');
              if (dropdown) dropdown.classList.remove('show');
              setShowLoginModal(true);
            };
          }
        }, 0);
      }
    };

    // Toggle account dropdown
    const toggleAccountDropdown = (event) => {
      console.log('toggleAccountDropdown called!', event);
      
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      
      const dropdown = document.getElementById('accountDropdown');
      const accountAction = document.getElementById('accountAction');
      
      console.log('Elements found:', {
        dropdown: !!dropdown,
        accountAction: !!accountAction,
        dropdownClassList: dropdown?.classList.toString()
      });
      
      if (dropdown) {
        const wasShown = dropdown.classList.contains('show');
        dropdown.classList.toggle('show');
        console.log('Dropdown state changed:', wasShown, '→', dropdown.classList.contains('show'));
        
        // Force visibility if toggle didn't work
        if (dropdown.classList.contains('show')) {
          dropdown.style.opacity = '1';
          dropdown.style.visibility = 'visible';
          dropdown.style.transform = 'translateY(0)';
          dropdown.style.pointerEvents = 'auto';
        }
      } else {
        console.error('Account dropdown element not found!');
      }
    };

    // Close dropdown when clicking outside
    const closeDropdownOnOutsideClick = (event) => {
      const dropdown = document.getElementById('accountDropdown');
      const accountAction = document.getElementById('accountAction');
      
      if (dropdown && accountAction && !accountAction.contains(event.target)) {
        dropdown.classList.remove('show');
      }
    };

    // Setup account button click handler with MutationObserver
    const retryTimeouts = [];
    let accountButtonSetup = false;
    let observer = null;
    
    const setupAccountButton = () => {
      const accountAction = document.getElementById('accountAction');
      console.log('Trying to setup account button, element found:', !!accountAction);
      
      if (accountAction) {
        console.log('Account action found! Attaching click handler');
        
        // Remove any existing listener first
        accountAction.removeEventListener('click', toggleAccountDropdown);
        
        // Add fresh listener
        accountAction.addEventListener('click', toggleAccountDropdown);
        accountAction.style.cursor = 'pointer';
        
        // Also add a direct onclick as fallback
        accountAction.onclick = (e) => {
          console.log('Direct onclick triggered!');
          toggleAccountDropdown(e);
        };
        
        accountButtonSetup = true;
        
        // Disconnect observer once setup is done
        if (observer) {
          observer.disconnect();
        }
        
        return true;
      }
      return false;
    };

    // Try immediately
    setupAccountButton();

    // Use MutationObserver to watch for DOM changes
    if (!accountButtonSetup) {
      observer = new MutationObserver((mutations) => {
        if (!accountButtonSetup) {
          setupAccountButton();
        }
      });

      // Observe the entire document for additions
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    // Also try with timeouts as fallback
    [100, 300, 500, 1000, 2000].forEach(delay => {
      const timeoutId = setTimeout(() => {
        if (!accountButtonSetup) {
          setupAccountButton();
        }
      }, delay);
      retryTimeouts.push(timeoutId);
    });

    // Setup My Orders link handler in dropdown
    const handleMyOrdersClick = (e) => {
      const target = e.target;
      const ordersLink = target.closest('#myOrdersLink') || 
                        (target.id === 'myOrdersLink' ? target : null);
      
      if (!ordersLink) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      // Check if user is logged in - get from localStorage
      try {
        const userDetails = JSON.parse(localStorage.getItem('userDetails'));
        const isUserLoggedIn = userDetails && userDetails.data && userDetails.data.customer;
        
        if (!isUserLoggedIn) {
          alert('Please login to view your orders');
          // Close dropdown
          const dropdown = document.getElementById('accountDropdown');
          if (dropdown) {
            dropdown.classList.remove('show');
          }
          return;
        }
      } catch (error) {
        console.error('Error checking login status:', error);
        alert('Please login to view your orders');
        // Close dropdown
        const dropdown = document.getElementById('accountDropdown');
        if (dropdown) {
          dropdown.classList.remove('show');
        }
        return;
      }
      
      // Close dropdown before navigation
      const dropdown = document.getElementById('accountDropdown');
      if (dropdown) {
        dropdown.classList.remove('show');
      }
      
      // Navigate to orders page using React Router
      navigate('/order');
    };

    // Setup logout handler in dropdown
    const handleAccountLogout = (e) => {
      const target = e.target;
      const logoutBtn = target.closest('#account-logout-btn');
      
      if (!logoutBtn) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      localStorage.removeItem('userDetails');
      localStorage.removeItem('authToken');
      localStorage.setItem('cartState', JSON.stringify({}));
      setIsLoggedIn(false);
      setCartItems([]);
      window.dispatchEvent(new Event('cart-updated'));
      window.dispatchEvent(new Event('user-logged-out'));
      
      // Close dropdown
      const dropdown = document.getElementById('accountDropdown');
      if (dropdown) {
        dropdown.classList.remove('show');
      }
      
      fetchCartItems();
      fetchHeaderContent();
      navigate('/');
    };

    // Setup login button handler in dropdown - using event delegation
    const handleAccountLogin = (e) => {
      const target = e.target;
      
      // Check if clicked element is login button or its child
      const loginBtn = target.closest('#account-login-btn') || 
                       (target.id === 'account-login-btn' ? target : null) ||
                       (target.classList.contains('login-btn') ? target : null);
      
      console.log('Click detected, login button found:', !!loginBtn, 'target:', target);
      
      if (!loginBtn) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      console.log('Opening login modal...');
      
      // Close account dropdown
      const dropdown = document.getElementById('accountDropdown');
      if (dropdown) {
        dropdown.classList.remove('show');
      }
      
      // Open login modal
      setShowLoginModal(true);
      setActiveTab("login");
      setShowOtpLogin(false);
      setPhoneNumber("");
      setOtp("");
      setFirstName("");
      setLastName("");
      setEmail("");
    };

    // Initialize dropdown content with retries
    const initializeDropdown = () => {
      updateAccountDropdown();
    };
    
    // Try immediately
    initializeDropdown();
    
    // Also retry after delays to ensure DOM is ready
    const initTimeouts = [100, 300, 500].map(delay => 
      setTimeout(initializeDropdown, delay)
    );
    
    // Setup event listeners
    document.addEventListener('click', closeDropdownOnOutsideClick);
    document.addEventListener('click', handleAccountLogout);
    document.addEventListener('click', handleAccountLogin);
    document.addEventListener('click', handleMyOrdersClick);
    
    // Listen for user login/logout events
    window.addEventListener('user-logged-in', updateAccountDropdown);
    window.addEventListener('user-logged-out', updateAccountDropdown);

    // Expose functions globally for HTML onclick if needed
    window.toggleAccountDropdown = toggleAccountDropdown;
    window.updateAccountDropdown = updateAccountDropdown;

    // Define main logout handler - using event delegation for reliability
    const handleLogout = (e) => {
      // Check if the clicked element is the logout button or inside it
      const target = e.target;
      const explicitLogoutBtn =
        target.closest('#logout-btn') || (target.id === 'logout-btn' ? target : null);

      // Fallback: any anchor/button whose visible text is "Logout"
      let textLogoutBtn = null;
      if (!explicitLogoutBtn) {
        const clickable = target.closest('a, button');
        if (clickable) {
          const label = (clickable.textContent || '').trim().toLowerCase();
          if (label === 'logout') {
            textLogoutBtn = clickable;
          }
        }
      }

      const logoutBtn = explicitLogoutBtn || textLogoutBtn;
      
      if (!logoutBtn) return;
      
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      localStorage.removeItem('userDetails');
      localStorage.removeItem('authToken');
      localStorage.setItem('cartState', JSON.stringify({}));
      setIsLoggedIn(false);
      setCartItems([]);
      // Dispatch cart-updated event to update cart count immediately
      window.dispatchEvent(new Event('cart-updated'));
      window.dispatchEvent(new Event('user-logged-out'));
      fetchCartItems();
      fetchHeaderContent();
      navigate('/');
    };

    // Use event delegation on document - this always exists and catches all clicks
    // Remove any existing listener first to prevent duplicates
    document.removeEventListener('click', handleLogout, true);
    // Add event listener with capture phase to catch early
    document.addEventListener('click', handleLogout, true);
    
    let timeoutId = null;
    
    // Also try direct attachment as fallback with a small delay
    const attachDirectListener = () => {
      const logoutBtn = document.getElementById('logout-btn');
      if (logoutBtn) {
        const directHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          localStorage.removeItem('userDetails');
          localStorage.removeItem('authToken');
          localStorage.setItem('cartState', JSON.stringify({}));
          setIsLoggedIn(false);
          setCartItems([]);
          window.dispatchEvent(new Event('cart-updated'));
          fetchCartItems();
          fetchHeaderContent();
          navigate('/');
        };
        // Remove any existing listener
        logoutBtn.removeEventListener('click', directHandler);
        // Add the event listener
        logoutBtn.addEventListener('click', directHandler, { once: false });
      }
    };
    
    // Try immediately
    attachDirectListener();
    // Also try after a small delay to ensure DOM is ready
    timeoutId = setTimeout(attachDirectListener, 100);

    // Add search functionality to header search input
    // Separate this from cartItems dependency to prevent search input from clearing
    const searchInput = document.querySelector('.search-box input[type="text"]');
    const searchButton = document.querySelector('.search-box .search-btn, .search-box button');
    let searchTimeout = null;

    const handleSearch = (searchValue) => {
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        searchTimeout = null;
      }

      const trimmedValue = searchValue ? searchValue.trim() : '';
      
      // Dispatch search event with the search query (even if empty to clear search)
      // Use bubbles: true and cancelable: true to ensure event propagates properly
      const searchEvent = new CustomEvent('header-search', {
        detail: { query: trimmedValue },
        bubbles: true,
        cancelable: true
      });
      
      // Dispatch event synchronously - listeners should be attached via capture phase
      window.dispatchEvent(searchEvent);
      console.log('Header.jsx - Search event dispatched:', trimmedValue);

      // Store search query in localStorage for persistence
      if (trimmedValue) {
        localStorage.setItem('headerSearchQuery', trimmedValue);
      } else {
        localStorage.removeItem('headerSearchQuery');
      }
    };

    // Define handlers outside if blocks so they're accessible in cleanup
    let handleInputChange = null;
    let handleKeyPress = null;
    let handleSearchClick = null;

    if (searchInput) {
      // Handle input change with debounce
      handleInputChange = (e) => {
        const value = e.target.value;
        
        // Clear previous timeout
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }

        // Debounce search - wait 500ms after user stops typing
        searchTimeout = setTimeout(() => {
          handleSearch(value);
        }, 500);
      };

      // Handle Enter key press
      handleKeyPress = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (searchTimeout) {
            clearTimeout(searchTimeout);
            searchTimeout = null;
          }
          handleSearch(e.target.value);
        }
      };

      searchInput.addEventListener('input', handleInputChange);
      searchInput.addEventListener('keypress', handleKeyPress);

      // Load saved search query if exists and input is empty
      if (!searchInput.value) {
        const savedQuery = localStorage.getItem('headerSearchQuery');
        if (savedQuery) {
          searchInput.value = savedQuery;
        }
      }
    }

    if (searchButton) {
      handleSearchClick = (e) => {
        e.preventDefault();
        if (searchInput) {
          if (searchTimeout) {
            clearTimeout(searchTimeout);
            searchTimeout = null;
          }
          handleSearch(searchInput.value);
        }
      };

      searchButton.addEventListener('click', handleSearchClick);
    }

    // Cleanup function
    return () => {
      // Remove event delegation listener from document
      document.removeEventListener('click', handleLogout, true);
      document.removeEventListener('click', closeDropdownOnOutsideClick);
      document.removeEventListener('click', handleAccountLogout);
      document.removeEventListener('click', handleAccountLogin);
      document.removeEventListener('click', handleMyOrdersClick);
      
      // Remove account action listener
      const accountAction = document.getElementById('accountAction');
      if (accountAction) {
        accountAction.removeEventListener('click', toggleAccountDropdown);
      }
      
      // Remove window event listeners
      window.removeEventListener('user-logged-in', updateAccountDropdown);
      window.removeEventListener('user-logged-out', updateAccountDropdown);
      
      // Clear all timeouts
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      retryTimeouts.forEach(id => clearTimeout(id));
      initTimeouts.forEach(id => clearTimeout(id));
      
      // Disconnect observer
      if (observer) {
        observer.disconnect();
      }
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        searchTimeout = null;
      }
      if (searchInput && handleInputChange) {
        searchInput.removeEventListener('input', handleInputChange);
      }
      if (searchInput && handleKeyPress) {
        searchInput.removeEventListener('keypress', handleKeyPress);
      }
      if (searchButton && handleSearchClick) {
        searchButton.removeEventListener('click', handleSearchClick);
      }
    };
  }, [headerHtml, navigate, fetchCartItems, fetchHeaderContent]); // Removed cartItems dependency to prevent search input from clearing

  // Add effect to force horizontal scroll on mobile navbar
  useEffect(() => {
    if (typeof window === 'undefined' || !headerHtml) return;

    const applyMobileNavScroll = () => {
      // Find all possible navbar nav elements
      const navSelectors = [
        '.navbar-nav',
        '.navbar-collapse .navbar-nav',
        '.collapse .navbar-nav',
        'nav ul',
        'ul.navbar-nav',
        '.navbar ul'
      ];

      navSelectors.forEach(selector => {
        const navElements = document.querySelectorAll(`.header-wrapper ${selector}`);
        navElements.forEach(nav => {
          if (window.innerWidth <= 768) {
            nav.style.overflowX = 'auto';
            nav.style.overflowY = 'hidden';
            nav.style.webkitOverflowScrolling = 'touch';
            nav.style.flexWrap = 'nowrap';
            nav.style.whiteSpace = 'nowrap';
            nav.style.display = 'flex';
            nav.style.flexDirection = 'row';
            
            // Ensure all children don't shrink
            const children = nav.querySelectorAll('li, .nav-item, .nav-link, a');
            children.forEach(child => {
              child.style.flexShrink = '0';
              child.style.whiteSpace = 'nowrap';
            });
          }
        });
      });

      // Also apply to collapse containers
      const collapseSelectors = [
        '.navbar-collapse',
        '.collapse'
      ];

      collapseSelectors.forEach(selector => {
        const collapseElements = document.querySelectorAll(`.header-wrapper ${selector}`);
        collapseElements.forEach(collapse => {
          if (window.innerWidth <= 768) {
            collapse.style.overflowX = 'auto';
            collapse.style.overflowY = 'hidden';
            collapse.style.webkitOverflowScrolling = 'touch';
            collapse.style.maxHeight = 'none';
            collapse.style.height = 'auto';
          }
        });
      });
    };

    // Apply immediately
    const timeout = setTimeout(applyMobileNavScroll, 100);

    // Also apply on window resize
    window.addEventListener('resize', applyMobileNavScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', applyMobileNavScroll);
    };
  }, [headerHtml]);

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      console.log("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/send_otp`,
        { customer: { login: phoneNumber } }
      );

      if (response.data) {
        setShowOtpLogin(true);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      console.log("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/verify_otp`,
        { customer: { login: phoneNumber, otp: otp } }
      );

      if (response.data) {
        // Check response message
        if (response.data.message === "Login successful") {
          // User exists, log them in
          const userData = {
            data: {
              customer: response.data.customer,
              token: response.data.token
            }
          };
          
          localStorage.setItem("userDetails", JSON.stringify(userData));
          localStorage.setItem("authToken", response.data.token);
          
          // Close modal properly
          const modalElement = document.getElementById("signupModal");
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
          setShowLoginModal(false);
          
          // Dispatch events
          window.dispatchEvent(new Event('user-logged-in'));
          window.dispatchEvent(new Event('cart-updated'));
          
          // Refresh header and cart after modal closes
          setTimeout(async () => {
            await fetchHeaderContent();
            await fetchCartItems();
          }, 300);
        } else {
          // New user, show signup fields
          setActiveTab("signup");
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    }
  };

  // Handle Sign Up
  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      console.log("Please fill in all required fields");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/create`,
        {
          customer: {
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phoneNumber,
            password: Math.random().toString(36).slice(-8) // Generate random password
          }
        }
      );

      if (response.data) {
        // After signup, need to login with OTP
        // The customer is created, now verify OTP to get token
        const verifyResponse = await sendRequest(
          "POST",
          `${BASE_URL}/v1/public_site/customers/verify_otp`,
          { customer: { login: phoneNumber, otp: otp } }
        );

        if (verifyResponse.data && verifyResponse.data.token) {
          // Store user data
          const userData = {
            data: {
              customer: verifyResponse.data.customer,
              token: verifyResponse.data.token
            }
          };
          
          localStorage.setItem("userDetails", JSON.stringify(userData));
          localStorage.setItem("authToken", verifyResponse.data.token);
          
          // Close modal properly
          const modalElement = document.getElementById("signupModal");
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
          setShowLoginModal(false);
          
          // Dispatch events
          window.dispatchEvent(new Event('user-logged-in'));
          window.dispatchEvent(new Event('cart-updated'));
          
          // Refresh header and cart after modal closes
          setTimeout(async () => {
            await fetchHeaderContent();
            await fetchCartItems();
          }, 300);
        }
      }
    } catch (error) {
      console.error("Error signing up:", error);
    }
  };

  // Use the reusable hook for link navigation
  const headerRef = useHtmlLinkNavigation(headerHtml);

  return (
    <>
      <div ref={headerRef} className="header-wrapper" dangerouslySetInnerHTML={{ __html: headerHtml }} />
      
      {/* Login/Signup Modal */}
      <SignupModal
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showOtpLogin={showOtpLogin}
        setShowOtpLogin={setShowOtpLogin}
        handleSignUp={handleSignUp}
        handleSendOtp={handleSendOtp}
        handleVerifyOtp={handleVerifyOtp}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        otp={otp}
        setOtp={setOtp}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        email={email}
        setEmail={setEmail}
      />
    </>
  );
};

export default Header;
